import { useDepartmentClients, useDepartmentWeek, useTimeDepartments } from '../api/time.ts';
import { describeMinutes } from '../duration.ts';
import { formatDate } from '../dates.ts';
import { useKeptState } from '../components/OpenTabs.tsx';
import { agencyToday } from '../components/tasks/FollowUp.tsx';
import { chip, plain } from '../components/tasks/Fields.tsx';
import { LoadFailure } from '../components/LoadFailure.tsx';
import { addDays, mondayOf, weekdayShort } from '../time/days.ts';
import { clientRange, type DepartmentRange } from '../time/view.ts';

/**
 * Department time (money plan phase 1, 161; Arthur, A-#331): a department's own work, for its director and tiers 1-2.
 * By person, a week; by client, a range, every client NAMED whoever it is assigned to. Only the department's own
 * entries (a task or action by its department; a visit Social Work; a site visit Property; a communication by the
 * department chosen when it was logged: Q9), never another department's minutes, and only minutes, dates, person and
 * client. The choices are kept while the tab is open, in memory only.
 */

interface Kept { department: string; view: 'person' | 'client'; week: string; range: DepartmentRange }
const FRESH: Kept = { department: '', view: 'person', week: '', range: 'week' };

export function DepartmentTime() {
  const open = useTimeDepartments();
  const departments = open.data?.departments ?? [];
  const [kept, setKept] = useKeptState<Kept>('departmentTime', FRESH);
  const department = departments.includes(kept.department) ? kept.department : departments[0] ?? '';
  const set = (patch: Partial<Kept>) => setKept((k) => ({ ...k, ...patch }));

  return (
    <div className="min-h-dvh bg-page">
      <div className="mx-auto max-w-[96rem] lg:px-6 lg:py-6">
        <header className="page-head">
          <h1 className="page-title">Department time</h1>
          <p className="mt-1 max-w-[60rem] text-sm text-neutral-700">
            The department&rsquo;s own work, on every client: tasks and actions, and the welfare visits (Social Work),
            site visits (Property) and communications logged under it. Minutes, dates, the person and the client only.
          </p>
        </header>
        {open.error && !open.data ? <LoadFailure what="Department time" error={open.error} onRetry={() => void open.refetch()} />
          : !open.data ? <p className="py-3 text-neutral-700">Loading…</p>
          : departments.length === 0 ? <p className="py-3 text-neutral-700">No department&rsquo;s time is open to your roles.</p>
          : (
            <>
              <div role="tablist" aria-label="Department" className="mt-3 flex flex-wrap gap-2">
                {departments.map((d) => (
                  <button key={d} type="button" role="tab" aria-selected={d === department} className={chip(d === department)}
                          onClick={() => set({ department: d })}>{d}</button>
                ))}
              </div>
              <div role="tablist" aria-label="View" className="mt-2 flex flex-wrap gap-2">
                <button type="button" role="tab" aria-selected={kept.view === 'person'} className={chip(kept.view === 'person')}
                        onClick={() => set({ view: 'person' })}>By person</button>
                <button type="button" role="tab" aria-selected={kept.view === 'client'} className={chip(kept.view === 'client')}
                        onClick={() => set({ view: 'client' })}>By client</button>
              </div>
              {kept.view === 'person'
                ? <ByPerson department={department} week={kept.week} setWeek={(week) => set({ week })} />
                : <ByClient department={department} range={kept.range} setRange={(range) => set({ range })} />}
            </>
          )}
      </div>
    </div>
  );
}

function ByPerson({ department, week, setWeek }: { department: string; week: string; setWeek: (w: string) => void }) {
  const q = useDepartmentWeek(department, week);
  const shown = q.data?.week ?? (week || mondayOf(agencyToday()));
  return (
    <section aria-labelledby="by-person" className="mt-4">
      <h2 id="by-person" className="sr-only">By person</h2>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={plain} onClick={() => setWeek(addDays(shown, -7))}>Previous week</button>
        <button type="button" className={plain} onClick={() => setWeek('')}>This week</button>
        <button type="button" className={plain} onClick={() => setWeek(addDays(shown, 7))}>Next week</button>
        <span className="text-neutral-900">Week of {formatDate(shown)}</span>
      </div>
      {q.error && !q.data ? <LoadFailure what="This department's week" error={q.error} onRetry={() => void q.refetch()} />
        : !q.data ? <p className="py-3 text-neutral-700">Loading…</p>
        : q.data.people.length === 0 ? <p className="py-3 text-neutral-700">No time logged in {department} this week.</p>
        : (
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="border-b border-neutral-300 text-left text-neutral-700">
                <th className="py-1.5 font-semibold">Person</th>
                {q.data.days.map((d) => <th key={d} className="py-1.5 font-semibold">{weekdayShort(d)} {formatDate(d)}</th>)}
                <th className="py-1.5 text-right font-semibold">Week</th>
              </tr>
            </thead>
            <tbody>
              {q.data.people.map((p) => (
                <tr key={p.staff_user_id} className="border-b border-neutral-200 text-neutral-900">
                  <td className="py-1.5">{p.person ?? 'Someone no longer on staff'}</td>
                  {p.days.map((m, i) => <td key={i} className="py-1.5 tabular-nums">{m ? describeMinutes(m) : '–'}</td>)}
                  <td className="py-1.5 text-right font-semibold tabular-nums">{describeMinutes(p.total)}</td>
                </tr>
              ))}
              <tr className="text-neutral-900">
                <td className="py-1.5 font-semibold" colSpan={8}>{department}, the week</td>
                <td className="py-1.5 text-right font-semibold tabular-nums">{describeMinutes(q.data.total)}</td>
              </tr>
            </tbody>
          </table>
        )}
    </section>
  );
}

function ByClient({ department, range, setRange }: { department: string; range: Kept['range']; setRange: (r: Kept['range']) => void }) {
  const q = useDepartmentClients(department, clientRange(range, agencyToday()));
  const ranges: { key: Kept['range']; label: string }[] = [
    { key: 'week', label: 'This week' }, { key: 'month', label: 'Last 31 days' }, { key: 'year', label: 'Last 12 months' }];
  return (
    <section aria-labelledby="by-client" className="mt-4">
      <h2 id="by-client" className="sr-only">By client</h2>
      <div role="tablist" aria-label="Period" className="flex flex-wrap gap-2">
        {ranges.map((r) => (
          <button key={r.key} type="button" role="tab" aria-selected={range === r.key} className={chip(range === r.key)}
                  onClick={() => setRange(r.key)}>{r.label}</button>
        ))}
      </div>
      {q.error && !q.data ? <LoadFailure what="This department's clients" error={q.error} onRetry={() => void q.refetch()} />
        : !q.data ? <p className="py-3 text-neutral-700">Loading…</p>
        : q.data.clients.length === 0
          ? <p className="py-3 text-neutral-700">No time logged in {department} between {formatDate(q.data.from)} and {formatDate(q.data.to)}.</p>
        : (
          <>
            <p className="mt-3 text-neutral-900">
              <span className="font-semibold">{describeMinutes(q.data.total)}</span> in {department} between{' '}
              {formatDate(q.data.from)} and {formatDate(q.data.to)}.
            </p>
            <ol className="mt-2 divide-y divide-neutral-200 rounded-lg border border-neutral-300">
              {q.data.clients.map((c) => (
                <li key={c.client_id ?? 'none'} className="p-3">
                  <div className="flex flex-wrap justify-between gap-2">
                    {/* named, never a link: the reader may not see this client's record (A-#331: the minutes only) */}
                    <span className="font-semibold text-neutral-900">
                      {c.client_id ? [c.client_ref, c.client_name].filter(Boolean).join(' ') : 'No client'}
                    </span>
                    <span className="font-semibold tabular-nums">{describeMinutes(c.minutes)}</span>
                  </div>
                  <p className="mt-1 text-sm text-neutral-700">
                    {c.people.map((p) => `${p.person ?? 'Someone no longer on staff'} ${describeMinutes(p.minutes)}`).join(' · ')}
                  </p>
                  {c.by_week.length > 1 && (
                    <p className="mt-1 text-sm text-neutral-700">
                      {c.by_week.map((w) => `week of ${formatDate(w.week)}: ${describeMinutes(w.minutes)}`).join(' · ')}
                    </p>
                  )}
                </li>
              ))}
            </ol>
          </>
        )}
    </section>
  );
}
